import { useEffect, useMemo, useState, useCallback } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  listBirthdays,
  getBirthdaysSummary,
  getBirthdayOptions,
  COMPANY_OPTIONS,
  BRANCH_OPTIONS,
  ACADEMIC_YEAR_OPTIONS,
  DATE_PERIOD_OPTIONS,
} from '../api/birthdaysApi'

const DEFAULT_PAGE_SIZE = 25

export function useBirthdaysListScreen() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  const [tab, setTab] = useState(searchParams.get('tab') || 'active')
  const [company, setCompany] = useState('All')
  const [branch, setBranch] = useState('All')
  const [academicYear, setAcademicYear] = useState('All')
  const [searchField, setSearchField] = useState('Name')
  const [searchText, setSearchText] = useState('')
  const [datePeriod, setDatePeriod] = useState('period')
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')

  const [options, setOptions] = useState({
    companyOptions: COMPANY_OPTIONS,
    branchOptions: BRANCH_OPTIONS,
    academicYearOptions: ACADEMIC_YEAR_OPTIONS,
    datePeriodOptions: DATE_PERIOD_OPTIONS,
  })

  const [rows, setRows] = useState([])
  const [totalCount, setTotalCount] = useState(0)
  const [totalPages, setTotalPages] = useState(1)
  const [summary, setSummary] = useState({ today: 0, thisWeek: 0, thisMonth: 0, total: 0 })
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')

  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)

  // Load filter options from server on mount
  useEffect(() => {
    let isMounted = true
    getBirthdayOptions()
      .then((opts) => {
        if (isMounted) {
          setOptions({
            companyOptions: opts.companyOptions || COMPANY_OPTIONS,
            branchOptions: opts.branchOptions || BRANCH_OPTIONS,
            academicYearOptions: opts.academicYearOptions || ACADEMIC_YEAR_OPTIONS,
            datePeriodOptions: opts.datePeriodOptions || DATE_PERIOD_OPTIONS,
          })
        }
      })
      .catch(() => {
        // Fallbacks already in place
      })
    return () => {
      isMounted = false
    }
  }, [])

  // Load live summary KPI metrics
  const loadSummary = useCallback(async () => {
    try {
      const sum = await getBirthdaysSummary()
      setSummary(sum)
    } catch {
      // Keep previous summary
    }
  }, [])

  useEffect(() => {
    loadSummary()
  }, [loadSummary])

  const queryParams = useMemo(
    () => ({
      company,
      branch,
      academicYear,
      searchField,
      searchText,
      period: datePeriod,
      fromDate,
      toDate,
      tab,
      page: currentPage,
      pageSize,
    }),
    [company, branch, academicYear, searchField, searchText, datePeriod, fromDate, toDate, tab, currentPage, pageSize]
  )

  const loadBirthdays = useCallback(async () => {
    setIsLoading(true)
    setError('')
    try {
      const res = await listBirthdays(queryParams)
      setRows(res.data || [])
      setTotalCount(res.total || 0)
      setTotalPages(res.totalPages || Math.max(1, Math.ceil((res.total || 0) / pageSize)))
    } catch {
      setError('Failed to load birthdays. Please try again.')
      setRows([])
      setTotalCount(0)
      setTotalPages(1)
    } finally {
      setIsLoading(false)
    }
  }, [queryParams, pageSize])

  useEffect(() => {
    loadBirthdays()
  }, [tab, currentPage, pageSize])

  const toISODate = (date) => {
    const d = new Date(date)
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  }

  const handleDatePeriodChange = (preset) => {
    setDatePeriod(preset)
    if (preset === 'period') {
      setFromDate('')
      setToDate('')
      return
    }

    const now = new Date()
    const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    const sunday = new Date(startOfDay)
    sunday.setDate(startOfDay.getDate() - ((startOfDay.getDay() + 6) % 7))

    const dayOffset = (days) => new Date(sunday.getFullYear(), sunday.getMonth(), sunday.getDate() + days)

    const ranges = {
      today: [startOfDay, startOfDay],
      yesterday: [new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1), new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1)],
      current_week: [dayOffset(0), dayOffset(6)],
      current_month: [new Date(now.getFullYear(), now.getMonth(), 1), new Date(now.getFullYear(), now.getMonth() + 1, 0)],
      current_year: [new Date(now.getFullYear(), 0, 1), new Date(now.getFullYear(), 11, 31)],
      last_week: [dayOffset(-7), dayOffset(-1)],
      last_month: [new Date(now.getFullYear(), now.getMonth() - 1, 1), new Date(now.getFullYear(), now.getMonth(), 0)],
      last_year: [new Date(now.getFullYear() - 1, 0, 1), new Date(now.getFullYear() - 1, 11, 31)],
      next_week: [dayOffset(7), dayOffset(13)],
      next_month: [new Date(now.getFullYear(), now.getMonth() + 1, 1), new Date(now.getFullYear(), now.getMonth() + 2, 0)],
      next_year: [new Date(now.getFullYear() + 1, 0, 1), new Date(now.getFullYear() + 1, 11, 31)],
    }

    const range = ranges[preset]
    if (range) {
      setFromDate(toISODate(range[0]))
      setToDate(toISODate(range[1]))
    }
  }

  const handleTabChange = (nextTab) => {
    setTab(nextTab)
    setSearchParams({ tab: nextTab })
    setCurrentPage(1)
  }

  const resetFilters = () => {
    setCompany('All')
    setBranch('All')
    setAcademicYear('All')
    setSearchField('Name')
    setSearchText('')
    setDatePeriod('period')
    setFromDate('')
    setToDate('')
    setCurrentPage(1)
    setTimeout(() => {
      loadBirthdays()
      loadSummary()
    }, 10)
  }

  const handleViewDetail = (studentId) => {
    navigate(`/admission/birthdays/entry?id=${studentId}`)
  }

  return {
    state: {
      rows,
      totalCount,
      summary,
      isLoading,
      error,
      tab,
      company,
      branch,
      academicYear,
      searchField,
      searchText,
      datePeriod,
      fromDate,
      toDate,
      currentPage,
      totalPages,
      pageSize,
      options,
    },
    actions: {
      setTab: handleTabChange,
      setCompany,
      setBranch,
      setAcademicYear,
      setSearchField,
      setSearchText,
      setFromDate,
      setToDate,
      handleDatePeriodChange,
      setCurrentPage,
      setPageSize: (v) => {
        setPageSize(Number(v))
        setCurrentPage(1)
      },
      search: () => {
        setCurrentPage(1)
        loadBirthdays()
      },
      resetFilters,
      handleViewDetail,
      refreshSummary: loadSummary,
    },
  }
}
